import { Component } from '@theme/component';

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  /** @type {(() => void) | undefined} */
  #resolveFinished;

  /** @type {Promise<void>} */
  finished = new Promise((resolve) => {
    this.#resolveFinished = resolve;
  });

  #finished = false;

  connectedCallback() {
    super.connectedCallback();
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.#finish();
      return;
    }

    const sourceRect = this.source?.getBoundingClientRect();
    const destinationRect = this.destination?.getBoundingClientRect();

    if (!sourceRect?.width || !sourceRect?.height || !destinationRect?.width || !destinationRect?.height) {
      this.#finish();
      return;
    }

    this.#animate(sourceRect, destinationRect);
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.left = `${startPoint.x}px`;
    this.style.top = `${startPoint.y}px`;

    if (typeof this.animate !== 'function') {
      this.#finish();
      return;
    }

    const travelX = endPoint.x - startPoint.x;
    const travelY = endPoint.y - startPoint.y;
    const lift = Math.min(72, Math.max(28, Math.abs(travelY) * 0.1));
    const duration = Math.min(720, Math.max(520, Math.hypot(travelX, travelY) * 0.55));

    try {
      const animation = this.animate(
        [
          {
            opacity: 0,
            transform: 'translate(-50%, -50%) translate3d(0, 0, 0) scale(0.92)',
            borderRadius: 'var(--style-border-radius-buttons-primary)',
          },
          {
            offset: 0.12,
            opacity: 1,
            transform: 'translate(-50%, -50%) translate3d(0, 0, 0) scale(1)',
          },
          {
            offset: 0.58,
            opacity: 1,
            transform: `translate(-50%, -50%) translate3d(${travelX * 0.58}px, ${travelY * 0.42 - lift}px, 0) scale(0.58)`,
          },
          {
            opacity: 0.15,
            transform: `translate(-50%, -50%) translate3d(${travelX}px, ${travelY}px, 0) scale(0.18)`,
            borderRadius: '50%',
          },
        ],
        {
          duration,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          fill: 'forwards',
        }
      );
      await animation.finished;
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[fly-to-cart] Animation ended unexpectedly:', error);
    } finally {
      this.#finish();
    }
  };

  #finish() {
    if (this.#finished) return;
    this.#finished = true;
    this.#resolveFinished?.();
    this.dispatchEvent(new CustomEvent('fly-to-cart:complete', { bubbles: true }));
    this.remove();
  }
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
